import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { CheckCircle2, PawPrint, Info } from "lucide-react";
import { useLang, localizeEta } from "../lib/i18n";

export default function OrderConfirmation({ order, paymentInfo, open, onClose }) {
  const { t, lang } = useLang();

  const renderNextSteps = () => {
    const m = order.payment_method;
    const amount = order.total.toFixed(0);
    const vodafonePhone = paymentInfo?.vodafone_cash_phone || "01042192135";
    const instapayPhone = paymentInfo?.payment_phone || "01024049798";
    const fawryPhone = paymentInfo?.fawry_wallet_phone || "01024049798";
    if (m === "Vodafone Cash") return t("checkout.vodafone_inst", { amount, phone: vodafonePhone });
    if (m === "Instapay") return t("checkout.instapay_inst", { amount, phone: instapayPhone });
    if (m === "FawryPay") return t("checkout.fawry_inst", { amount, phone: fawryPhone });
    return t("checkout.cod_inst", { amount });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg"
        style={{ background: "#FFFBF2", border: "2px solid #0F172A", borderRadius: 20 }}
        data-testid="order-confirmation"
      >
        <VisuallyHidden>
          <DialogTitle>Order Confirmation</DialogTitle>
          <DialogDescription>Your order has been placed successfully.</DialogDescription>
        </VisuallyHidden>
        <div className="text-center py-2">
          <div
            className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center bounce-y"
            style={{ background: "#A3E635", border: "2px solid #0F172A" }}
          >
            <CheckCircle2 size={36} />
          </div>
          <h3 className="font-display text-3xl font-bold mt-4">{t("confirm.title")}</h3>
          <p className="text-sm text-slate-600 mt-1">
            {t("confirm.message_pre")}{" "}
            <strong dir="ltr" style={{ unicodeBidi: "embed" }}>{order.whatsapp || order.phone}</strong>{" "}
            {t("confirm.message_post")}
          </p>

          <div
            className="neo-flat mt-4 p-3 text-start flex gap-2 items-start"
            style={{ background: "#FDE047", border: "2px solid #0F172A" }}
            data-testid="confirm-next-steps"
          >
            <Info size={18} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-display font-bold mb-1">{t("confirm.next_steps")}</div>
              <div>{renderNextSteps()}</div>
              {order.payment_reference && (
                <div className="mt-1 text-xs text-slate-700">
                  Ref: <strong>{order.payment_reference}</strong>
                </div>
              )}
            </div>
          </div>

          <div className="neo-flat mt-4 p-4 text-start">
            <div className="flex justify-between text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <span>{t("confirm.order_number")}</span>
              <span>{t("confirm.status")}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <div className="font-display text-xl font-bold" data-testid="order-number">
                {order.order_number}
              </div>
              <span
                className="text-xs font-bold px-2 py-1 rounded-md"
                style={{ background: "#38BDF8", color: "#0F172A", border: "2px solid #0F172A" }}
              >
                {order.status}
              </span>
            </div>

            <div className="mt-4 space-y-1 text-sm">
              <Row label={t("confirm.customer")} value={order.full_name} />
              <Row label={t("confirm.city_area")} value={`${order.city} • ${order.area}`} />
              <Row
                label={t("confirm.shipping")}
                value={`${order.shipping_method} (${localizeEta(order.shipping_eta, lang)}) — ${order.shipping_fee} EGP`}
              />
              <Row label={t("confirm.payment")} value={t(`payments.${order.payment_method}`)} />
              <Row label={t("confirm.pay_status")} value={order.payment_status || "—"} />
              <Row label={t("confirm.subtotal")} value={`${order.subtotal.toFixed(0)} EGP`} />
              <div className="flex justify-between font-display text-base font-bold pt-2 border-t border-dashed border-slate-300 mt-1">
                <span>{t("confirm.total")}</span>
                <span>{order.total.toFixed(0)} EGP</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-4 inline-flex items-center gap-1">
            <PawPrint size={12} /> {t("confirm.save_note")}
          </p>
          <button
            onClick={onClose}
            className="neo-btn w-full mt-4 py-3 font-bold text-white"
            style={{ background: "var(--pm-dog)" }}
            data-testid="close-confirmation-btn"
          >
            {t("confirm.continue")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-end">{value}</span>
    </div>
  );
}
